var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { cloneDeep, extend, find, forEach, get, isArray, isEmpty, isFunction, isObject, isUndefined, map, set, throttle, reduce, clone, has } from 'lodash';
import { Stratus } from '@stratusjs/runtime/stratus';
import { ErrorBase } from '@stratusjs/core/errors/errorBase';
import { ModelBase } from '@stratusjs/core/datastore/modelBase';
import { EventManager } from '@stratusjs/core/events/eventManager';
import { cookie } from '@stratusjs/core/environment';
import { isJSON, ucfirst } from '@stratusjs/core/misc';
import { XHR } from '@stratusjs/core/datastore/xhr';
import { Model } from './model';
import Toastify from 'toastify-js';
export const CollectionOptionKeys = ["autoSave", "autoSaveInterval", "cache", "direct", "target", "targetSuffix", "urlRoot", "watch", "payload", "convoy", "headers"];
export class Collection extends EventManager {
    constructor(options = {}) {
        super();
        this.name = 'Collection';
        this.direct = false;
        this.target = null;
        this.targetSuffix = null;
        this.urlRoot = '/Api';
        this.toast = true;
        this.qualifier = '';
        this.serviceId = null;
        this.infinite = false;
        this.threshold = 0.5;
        this.decay = 0;
        this.header = new ModelBase();
        this.meta = new ModelBase();
        this.model = Model;
        this.models = [];
        this.types = [];
        this.withCredentials = false;
        this.headers = {};
        this.cacheResponse = {};
        this.cacheHeaders = {};
        this.cache = false;
        this.pending = false;
        this.error = false;
        this.completed = false;
        this.filtering = false;
        this.paginate = false;
        this.watch = false;
        this.autoSave = false;
        this.autoSaveInterval = 2500;
        this.throttle = throttle(this.fetch, 1000);
        options = (!options || typeof options !== 'object') ? {} : options;
        extend(this, options);
        if (this.target) {
            this.urlRoot += '/' + ucfirst(this.target);
        }
        if (options.convoy) {
            const convoy = isJSON(options.convoy) ? JSON.parse(options.convoy) : options.convoy;
            if (isObject(convoy)) {
                this.meta.set(convoy.meta || {});
                const models = convoy.payload;
                if (isArray(models)) {
                    this.inject(models);
                    this.completed = true;
                }
                else {
                    console.error('malformed payload:', models);
                }
            }
            else {
                console.error('malformed convoy:', convoy);
            }
        }
        if (options.payload) {
            const models = isJSON(options.payload) ? JSON.parse(options.payload) : options.payload;
            if (isArray(models)) {
                this.inject(models);
                this.completed = true;
            }
            else {
                console.error('malformed payload:', models);
            }
        }
    }
    sanitizeOptions(options) {
        const sanitizedOptions = {};
        forEach(CollectionOptionKeys, (key) => {
            const data = get(options, key);
            if (isUndefined(data)) {
                return;
            }
            set(sanitizedOptions, key, data);
        });
        return sanitizedOptions;
    }
    serialize(obj, chain) {
        const str = [];
        obj = obj || {};
        forEach(obj, (value, key) => {
            if (isObject(value)) {
                if (chain) {
                    key = chain + '[' + key + ']';
                }
                str.push(this.serialize(value, key));
            }
            else {
                let encoded = '';
                if (chain) {
                    encoded += chain + '[';
                }
                encoded += key;
                if (chain) {
                    encoded += ']';
                }
                str.push(encoded + '=' + value);
            }
        });
        return str.join('&');
    }
    url() {
        return this.urlRoot + (this.targetSuffix || '');
    }
    inject(data, type) {
        if (!isArray(data)) {
            return;
        }
        if (this.types && this.types.indexOf(type) === -1) {
            this.types.push(type);
        }
        if (!this.direct) {
            data.forEach((target) => {
                this.models.push(new Model({
                    autoSave: this.autoSave,
                    autoSaveInterval: this.autoSaveInterval,
                    collection: this,
                    completed: true,
                    received: true,
                    toast: this.toast,
                    type: type || null,
                    watch: this.watch
                }, target));
            });
        }
    }
    sync(action, data, options) {
        this.pending = true;
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            action = action || 'GET';
            options = options || {};
            const request = {
                method: action,
                url: this.url(),
                headers: clone(this.headers),
                withCredentials: this.withCredentials,
            };
            if (!isUndefined(data)) {
                if (action === 'GET') {
                    if (isObject(data) && Object.keys(data).length) {
                        request.url += request.url.includes('?') ? '&' : '?';
                        request.url += this.serialize(data);
                    }
                }
                else {
                    request.headers['Content-Type'] = 'application/json';
                    request.data = JSON.stringify(data);
                }
            }
            if (Object.prototype.hasOwnProperty.call(options, 'headers') && typeof options.headers === 'object') {
                Object.keys(options.headers).forEach((headerKey) => {
                    request.headers[headerKey] = options.headers[headerKey];
                });
            }
            const queryHash = `${request.method}:${request.url}`;
            if (options.nocache) {
                if (queryHash in this.cacheResponse) {
                    delete this.cacheResponse[queryHash];
                }
                if (queryHash in this.cacheHeaders) {
                    delete this.cacheHeaders[queryHash];
                }
            }
            this.xhr = new XHR(request);
            const handler = (response) => {
                if (!isObject(response) && !isArray(response)) {
                    const error = new ErrorBase({
                        payload: response,
                        message: `Invalid Payload: ${request.method} ${request.url}`
                    }, {});
                    this.error = true;
                    this.pending = false;
                    this.throttleTrigger('change');
                    this.trigger('error', error);
                    reject(error);
                    return;
                }
                let responseHeaders = null;
                if (this.cache && request.method === 'GET') {
                    if (!(queryHash in this.cacheResponse)) {
                        this.cacheResponse[queryHash] = cloneDeep(response);
                    }
                    if (!(queryHash in this.cacheHeaders)) {
                        this.cacheHeaders[queryHash] = this.xhr.getAllResponseHeaders();
                    }
                    else {
                        responseHeaders = this.cacheHeaders[queryHash];
                    }
                }
                this.header.set(responseHeaders || this.xhr.getAllResponseHeaders());
                this.meta.set(response.meta || {});
                this.models = [];
                const payload = response.payload || response;
                this.error = false;
                if ((this.meta.has('success') && !this.meta.get('success'))) {
                    this.error = true;
                }
                else if (this.direct) {
                    this.models = payload;
                }
                else if (isArray(payload)) {
                    this.inject(payload);
                }
                else if (isObject(payload)) {
                    forEach(payload, (value, key) => {
                        this.inject(value, key);
                    });
                }
                else {
                    if (!this.meta.has('status') && !this.meta.has('success')) {
                        this.error = true;
                    }
                    console.warn(`Invalid Payload: ${request.method} ${request.url}`);
                }
                this.pending = false;
                this.completed = true;
                this.filtering = !isEmpty(this.meta.get('api.q'));
                this.paginate = !isEmpty(this.meta.get('api.p'));
                this.meta.clearTemp();
                this.throttleTrigger('change');
                this.trigger('complete');
                resolve(this.models);
            };
            if (this.cache && request.method === 'GET' && queryHash in this.cacheResponse) {
                handler(this.cacheResponse[queryHash]);
                return;
            }
            this.xhr.send()
                .then(handler)
                .catch((error) => {
                console.error(`XHR: ${request.method} ${request.url}`);
                this.throttleTrigger('change');
                this.trigger('error', error);
                reject(error);
                return;
            });
        }));
    }
    fetch(action, data, options) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            this.sync(action, data || this.meta.get('api'), options)
                .then(resolve)
                .catch((error) => __awaiter(this, void 0, void 0, function* () {
                console.error('FETCH:', error);
                if (!this.toast) {
                    reject(error);
                    return;
                }
                const errorMessage = this.errorMessage(error);
                const formatMessage = errorMessage ? `: ${errorMessage}` : '.';
                Toastify({
                    text: `Unable to Fetch ${this.target}${formatMessage}`,
                    duration: 12000,
                    close: true,
                    stopOnFocus: true,
                    style: {
                        background: '#E14D45',
                    }
                }).showToast();
                reject(error);
                return;
            }));
        }));
    }
    filter(query) {
        this.filtering = !isEmpty(query);
        this.meta.set('api.q', !isUndefined(query) ? query : '');
        this.meta.set('api.p', 1);
        return this.fetch();
    }
    throttleFilter(query) {
        this.meta.set('api.q', !isUndefined(query) ? query : '');
        return new Promise((resolve, reject) => {
            const request = this.throttle();
            if (cookie('env')) {
                console.log('request:', request);
            }
            request.then((models) => {
                if (cookie('env')) {
                }
                resolve(models);
            }).catch(reject);
        });
    }
    page(page) {
        this.paginate = !isEmpty(page);
        this.meta.set('api.p', page);
        this.fetch().then();
        delete this.meta.get('api').p;
    }
    toJSON() {
        return !this.direct ? this.models.map((model) => model.toJSON()) : this.models;
    }
    add(target, options) {
        if (!isObject(target)) {
            console.error('collection.add: target object not set!');
            return;
        }
        if (!options || typeof options !== 'object') {
            options = {};
        }
        if (target instanceof Model) {
            target.collection = this;
        }
        else {
            options.collection = this;
            target = new Model(options, target);
            target.initialize();
            if (options.autoSave || options.watch) {
                if (target.isNew()) {
                    target.save();
                }
                else if (!target.completed) {
                    target.fetch();
                }
            }
        }
        if (options.save) {
            target.save();
        }
        if (options.prepend) {
            this.models.unshift(target);
        }
        else {
            this.models.push(target);
        }
        if (options.trigger) {
            this.trigger('add', target);
        }
        this.throttleTrigger('change');
        return target;
    }
    remove(target) {
        if (!this.direct) {
            this.models.splice(this.models.indexOf(target), 1);
            this.throttleTrigger('change');
        }
        return this;
    }
    find(predicate) {
        return find(this.models, isFunction(predicate) ? predicate : (model) => model.get('id') === predicate);
    }
    map(predicate) {
        return map(this.models, model => model instanceof Model ? model.get(predicate) : null);
    }
    pluck(attribute) {
        return map(this.models, model => model instanceof Model ? model.pluck(attribute) : null);
    }
    exists(attribute) {
        return !!reduce(this.pluck(attribute) || [], (memo, data) => memo || !isUndefined(data));
    }
    errorMessage(error) {
        if (error instanceof ErrorBase) {
            console.error(`[${error.code}] ${error.message}`, error);
            return error.code !== 'Internal' ? error.message : null;
        }
        const digest = (error.responseText && isJSON(error.responseText)) ? JSON.parse(error.responseText) : null;
        if (!digest) {
            return null;
        }
        const message = get(digest, 'meta.status[0].message') || get(digest, 'error.exception[0].message') || null;
        if (!message) {
            return null;
        }
        if (!cookie('env') && has(digest, 'error.exception[0].message')) {
            console.error('[xhr] server:', message);
            return null;
        }
        return message;
    }
}
Stratus.Services.Collection = [
    '$provide',
    ($provide) => {
        $provide.factory('Collection', [() => Collection]);
    }
];
Stratus.Data.Collection = Collection;

//# sourceMappingURL=collection.js.map
